import axios from "axios";
import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { formatApiError } from "@/lib/adminAuth";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const portalApi = axios.create({ baseURL: `${BACKEND_URL}/api` });

portalApi.interceptors.request.use((config) => {
  const token = localStorage.getItem("tc_portal_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Mismo criterio que en adminAuth: un 401 en cualquier llamada cierra la sesión
// del portal, salvo que la llamada sea el login mismo (para no tapar el mensaje
// de "email o contraseña incorrectos" con una redirección).
let limpiarSesionPortal = () => {};
export const registrarLimpiadorSesionPortal = (fn) => { limpiarSesionPortal = fn; };

portalApi.interceptors.response.use(
  (res) => res,
  (err) => {
    const status = err?.response?.status;
    const esLogin = (err?.config?.url || "").includes("/portal/login");
    if (status === 401 && !esLogin) limpiarSesionPortal();
    return Promise.reject(err);
  },
);

export { formatApiError };

const PortalAuthContext = createContext(null);

export const PortalAuthProvider = ({ children }) => {
  const [usuario, setUsuario] = useState(null); // null=checking, false=no auth, { usuario, cliente }
  const [cliente, setCliente] = useState(null);
  const [errorSesion, setErrorSesion] = useState(false);

  const logout = useCallback(() => {
    localStorage.removeItem("tc_portal_token");
    setUsuario(false);
    setCliente(null);
    setErrorSesion(false);
  }, []);

  const cargarSesion = useCallback(async () => {
    const token = localStorage.getItem("tc_portal_token");
    if (!token) {
      setUsuario(false);
      return;
    }
    setErrorSesion(false);
    try {
      const { data } = await portalApi.get("/portal/me");
      setUsuario(data.usuario);
      setCliente(data.cliente);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        localStorage.removeItem("tc_portal_token");
        setUsuario(false);
      } else {
        setErrorSesion(true);
      }
    }
  }, []);

  useEffect(() => {
    registrarLimpiadorSesionPortal(logout);
  }, [logout]);

  useEffect(() => {
    cargarSesion();
  }, [cargarSesion]);

  const login = async (email, password) => {
    const { data } = await portalApi.post("/portal/login", { email, password });
    localStorage.setItem("tc_portal_token", data.token);
    setUsuario(data.usuario);
    setCliente(data.cliente);
    return data.usuario;
  };

  return (
    <PortalAuthContext.Provider value={{ usuario, cliente, errorSesion, reintentarSesion: cargarSesion, login, logout }}>
      {children}
    </PortalAuthContext.Provider>
  );
};

export const usePortalAuth = () => useContext(PortalAuthContext);
